import { createContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useOrg } from '@/shared/org';
import type {
  ModuleLimits,
  PlanState,
  Subscription,
  SubscriptionStatus,
} from './types';

/**
 * What the active organization may do, loaded once per organization.
 *
 * It sits under `OrgProvider` and re-reads on every switch, because the plan
 * belongs to the tenant: the same person moving from their employer's
 * organization to their own personal one changes plan mid-session, and every
 * limit on screen has to change with them.
 *
 * `plan_modules` is read rather than calling `org_module_limits` once per
 * module: a screen needs several modules' ceilings at mount, and N round trips
 * to render one page is the kind of thing that only becomes visible once a
 * customer has real data. The merge below repeats the SQL function's rule —
 * shallow, right-biased, add-on wins key by key — and the database stays the
 * enforcement either way.
 */
export const PlanContext = createContext<PlanState | null>(null);

const NO_LIMITS: Readonly<Record<string, ModuleLimits>> = {};

/** Narrow a jsonb blob to limits without trusting its shape. */
function toLimits(value: unknown): ModuleLimits {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, number | null> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (raw === null) out[key] = null;
    else if (typeof raw === 'number' && Number.isFinite(raw)) out[key] = raw;
  }
  return out;
}

export function PlanProvider({ children }: { children: ReactNode }) {
  const org = useOrg();
  const orgId = org.status === 'ready' ? org.orgId : null;

  const [planCode, setPlanCode] = useState('free');
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [limits, setLimits] = useState<Readonly<Record<string, ModuleLimits>>>(NO_LIMITS);
  const [status, setStatus] = useState<PlanState['status']>('loading');
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!orgId) {
      setStatus(org.status === 'loading' ? 'loading' : 'unavailable');
      setPlanCode('free');
      setSubscription(null);
      setLimits(NO_LIMITS);
      return;
    }

    let active = true;
    setStatus('loading');

    void (async () => {
      const [planResult, subscriptionResult, entitlementResult] = await Promise.all([
        supabase.rpc('org_plan', { p_org_id: orgId }),
        supabase
          .from('subscriptions')
          .select('plan_code, status, interval, trial_ends_at, current_period_end, cancel_at_period_end')
          .eq('org_id', orgId)
          .maybeSingle(),
        supabase
          .from('org_entitlements')
          .select('module_key, limits, expires_at')
          .eq('org_id', orgId),
      ]);

      if (!active) return;

      // The RPC is the authority on which plan is in force — a lapsed
      // subscription still has a row, and duplicating that rule here is how the
      // interface and the enforcement triggers would eventually disagree.
      if (planResult.error) {
        setStatus('unavailable');
        return;
      }
      const code = planResult.data ?? 'free';
      setPlanCode(code);

      const row = subscriptionResult.data;
      setSubscription(
        row
          ? {
              planCode: row.plan_code,
              status: row.status as SubscriptionStatus,
              interval: (row.interval as 'month' | 'year' | null) ?? null,
              trialEndsAt: row.trial_ends_at,
              currentPeriodEnd: row.current_period_end,
              cancelAtPeriodEnd: row.cancel_at_period_end,
            }
          : null,
      );

      const { data: modules, error: modulesError } = await supabase
        .from('plan_modules')
        .select('module_key, limits')
        .eq('plan_code', code);

      if (!active) return;
      if (modulesError) {
        setStatus('unavailable');
        return;
      }

      const merged: Record<string, ModuleLimits> = {};
      for (const module of modules ?? []) {
        merged[module.module_key] = toLimits(module.limits);
      }

      const now = Date.now();
      for (const entitlement of entitlementResult.data ?? []) {
        // Expiry is re-checked here because the row stays after it lapses: the
        // back-office needs the history, the product must not honour it.
        if (entitlement.expires_at && Date.parse(entitlement.expires_at) <= now) continue;
        merged[entitlement.module_key] = {
          ...(merged[entitlement.module_key] ?? {}),
          ...toLimits(entitlement.limits),
        };
      }

      setLimits(merged);
      setStatus('ready');
    })();

    return () => {
      active = false;
    };
  }, [orgId, org.status, nonce]);

  const value = useMemo<PlanState>(() => {
    const hasModule = (moduleKey: string) =>
      Object.prototype.hasOwnProperty.call(limits, moduleKey);

    const limit = (moduleKey: string, key: string): number | null => {
      const moduleLimits = limits[moduleKey];
      if (!moduleLimits) return null;
      return moduleLimits[key] ?? null;
    };

    return {
      status,
      planCode,
      subscription,
      limits,
      hasModule,
      limit,
      reload: () => setNonce((n) => n + 1),
    };
  }, [status, planCode, subscription, limits]);

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
}
